import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ClipboardList,
  CreditCard,
  FileText,
  Library,
  FolderOpen,
  Headphones,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  UsersRound,
  ShieldCheck,
  CalendarClock,
  Wallet,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button, Logo } from "@mbbe/ui";
import { useCustomerAuth } from "./CustomerAuthContext";
import { LanguageControl } from "./components/LanguageControl";
import { usePortalLanguagePreference } from "./PortalLanguagePreference";
import { usePortalCopy } from "./usePortalCopy";
import { NotificationBell } from "../destek/components/NotificationBell";
import { useSupportSurface } from "../destek/SupportSurfaceContext";
import { usePortalProject } from "./PortalProjectContext";
import {
  hasPortalCapability,
  portalMemberName,
  portalOrganizationName,
} from "./lib/portalAccess";

export function PortalLayout({ children }: { children: ReactNode }) {
  const { me, signOut } = useCustomerAuth();
  const { language, p } = usePortalCopy();
  const languagePreference = usePortalLanguagePreference();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef(true);
  const memberName = portalMemberName(me);
  const organizationName = portalOrganizationName(me);
  const memberEmail = me?.member?.email || me?.email || "";
  const supportLabel = language === "tr" ? "Destek" : "Support";
  const { routes: supportRoutes } = useSupportSurface();
  const projectState = usePortalProject();
  const scopeLabel = (() => {
    if (projectState.scope === "account") return p("projectScopeAccount");
    switch (projectState.status) {
      case "ready": return projectState.project?.name ?? null;
      case "loading": return p("projectScopeLoading");
      case "unavailable":
      case "malformed": return p("projectUnavailableHeader");
      case "unverified": return p("projectScopeSelected");
      default: return projectState.scope === "filtered" ? p("projectScopeAll") : null;
    }
  })();

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const drawer = drawerRef.current;
    const focusable = drawer?.querySelectorAll<HTMLElement>(
      'a,button,[tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || !focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
      if (restoreFocusRef.current) triggerRef.current?.focus();
      restoreFocusRef.current = true;
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeAtDesktop = (event: MediaQueryListEvent | MediaQueryList) => {
      if (!event.matches) return;
      restoreFocusRef.current = false;
      setOpen(false);
    };
    closeAtDesktop(desktop);
    desktop.addEventListener("change", closeAtDesktop);
    return () => desktop.removeEventListener("change", closeAtDesktop);
  }, []);

  const groups = [
    { label: language === "tr" ? "Genel" : "Overview", items: [
      hasPortalCapability(me, "dashboard") && { to: "/", label: p("dashboard"), icon: LayoutDashboard, active: location.pathname === "/" },
    ] },
    { label: language === "tr" ? "İş akışı" : "Workflow", items: [
      hasPortalCapability(me, "support") && { to: supportRoutes.list, label: supportLabel, icon: Headphones, active: location.pathname === supportRoutes.list || location.pathname.startsWith(`${supportRoutes.list}/`) },
      hasPortalCapability(me, "tasks") && { to: "/tasks", label: p("tasks"), icon: ClipboardList, active: location.pathname === "/tasks" || location.pathname.startsWith("/tasks/") },
      hasPortalCapability(me, "meetings") && { to: "/meetings", label: language === "tr" ? "Randevular" : "Meetings", icon: CalendarClock, active: location.pathname.startsWith("/meetings") },
      hasPortalCapability(me, "approvals") && { to: "/approvals", label: language === "tr" ? "Onaylar" : "Approvals", icon: ShieldCheck, active: location.pathname.startsWith("/approvals") },
    ] },
    { label: language === "tr" ? "Bilgi ve içerik" : "Knowledge and content", items: [
      hasPortalCapability(me, "reports") && { to: "/reports", label: language === "tr" ? "Raporlar" : "Reports", icon: FileText, active: location.pathname.startsWith("/reports") },
      hasPortalCapability(me, "files") && { to: "/files", label: p("files"), icon: FolderOpen, active: location.pathname === "/files" },
      hasPortalCapability(me, "documentation") && { to: "/documents", label: language === "tr" ? "Dokümantasyon" : "Documentation", icon: Library, active: location.pathname.startsWith("/documents") },
    ] },
    { label: language === "tr" ? "Ticari" : "Commercial", items: [
      hasPortalCapability(me, "payments") && { to: "/payments", label: language === "tr" ? "Ödemeler" : "Payments", icon: CreditCard, active: location.pathname.startsWith("/payments") },
      hasPortalCapability(me, "commercial") && { to: "/commercial", label: language === "tr" ? "Sözleşme" : "Commercial", icon: Wallet, active: location.pathname.startsWith("/commercial") },
    ] },
    { label: language === "tr" ? "Yönetim" : "Administration", items: [
      me?.member?.role === "owner" && { to: "/team", label: language === "tr" ? "Ekip" : "Team", icon: UsersRound, active: location.pathname.startsWith("/team") },
    ] },
  ].map((group) => ({ ...group, items: group.items.filter(Boolean) as Array<{ to: string; label: string; icon: typeof LayoutDashboard; active: boolean }> })).filter((group) => group.items.length);

  const navigation = <div className="space-y-5">{groups.map((group) => <section key={group.label} aria-label={group.label}>
    <p aria-hidden="true" className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">{group.label}</p>
    <div className="space-y-1">{group.items.map((item) => { const Icon = item.icon; return <Link key={item.to} to={projectState.hrefFor(item.to)} aria-current={item.active ? "page" : undefined} data-active={item.active || undefined} onClick={() => setOpen(false)} className={`flex min-h-11 items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm font-medium portal-motion-feedback ${item.active ? "border-brand-400 bg-brand-500/10 text-brand-200 ring-1 ring-inset ring-brand-500/20" : "border-transparent text-slate-400 hover:bg-white/5 hover:text-white"}`}><Icon aria-hidden size={18} />{item.label}</Link>; })}</div>
  </section>)}</div>;

  return (
    <div className="min-h-screen bg-dark-900 text-white">
      <a href="#portal-main" className="sr-only z-[60] rounded-md bg-brand-600 px-4 py-2 text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4">
        {language === "tr" ? "Ana içeriğe geç" : "Skip to main content"}
      </a>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(50rem_34rem_at_75%_-12%,rgba(59,130,246,0.09),transparent_62%)]"
      />
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] border-r border-slate-800 bg-slate-950/80 p-5 backdrop-blur-md md:flex md:flex-col">
        <Link to="/" className="mb-10 flex items-center gap-3">
          <Logo variant="contained" className="h-9 w-9" />
          <div>
            <div className="font-bold">mbbe</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-300">
              {p("product")}
            </div>
          </div>
        </Link>
        <nav aria-label={p("workspace")} className="min-h-0 flex-1 overflow-y-auto">
          {navigation}
        </nav>
        <div className="border-t border-slate-800 pt-4">
          <p className="truncate text-sm font-medium">{memberName}</p>
          {organizationName && (
            <p className="mt-1 truncate text-xs text-brand-300">
              {organizationName}
            </p>
          )}
          <p className="mt-1 truncate text-xs text-slate-500">{memberEmail}</p>
        </div>
      </aside>

      {open && (
        <button
          type="button"
          aria-label={p("closeMenu")}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
        />
      )}
      {open && (
        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={language === "tr" ? "Mobil çalışma alanı menüsü" : "Mobile workspace menu"}
          className="fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col border-r border-slate-700 bg-slate-950 p-5 shadow-2xl md:hidden"
        >
          <div className="mb-9 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo variant="contained" className="h-9 w-9" />
              <span className="font-bold">mbbe</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={p("closeMenu")}
              className="min-h-11 min-w-11 rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <X size={20} />
            </button>
          </div>
          <nav aria-label={p("workspace")} className="min-h-0 flex-1 overflow-y-auto">{navigation}</nav>
          <div className="border-t border-slate-800 pt-4">
            <p className="truncate text-sm font-medium">{memberName}</p>
            {organizationName && (
              <p className="mt-1 truncate text-xs text-brand-300">
                {organizationName}
              </p>
            )}
            <p className="mt-1 truncate text-xs text-slate-500">
              {memberEmail}
            </p>
          </div>
        </aside>
      )}

      <div className="relative md:pl-[260px]">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-800 bg-dark-900/85 px-4 backdrop-blur-md sm:px-6">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label={p("menu")}
            aria-expanded={open}
            className="min-h-11 min-w-11 rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 md:hidden"
          >
            <Menu size={21} />
          </button>
          <div className="min-w-0">
            <p className="hidden text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 md:block">{p("workspace")}</p>
            {scopeLabel && <p data-testid="portal-scope" className="max-w-[12rem] truncate text-xs text-slate-500" title={scopeLabel}><span className="sr-only">{p("projectScopeLabel")}: </span>{scopeLabel}</p>}
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <LanguageControl onChange={languagePreference.select} />
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              aria-label={p("signOut")}
              className="min-h-11 min-w-11"
            >
              <LogOut aria-hidden size={16} />
              <span className="ml-2 hidden sm:inline">{p("signOut")}</span>
            </Button>
          </div>
        </header>
        <main id="portal-main" tabIndex={-1} className="mx-auto min-h-[calc(100vh-4rem)] max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          {children}
        </main>
      </div>
    </div>
  );
}
